import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';

import styled from 'styled-components';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { Box } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import { noop } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';
import VirtualizedTree from '@adsk/nirvana-virtualized-tree';

import MalwareContent from './MalwareContent';
import EntityPickerModalError from '../../../modal/EntityPickerModalError';
import { applyBuiltInRules } from '../helpers/builtInRules';
import { formatEntityBlock } from '../helpers/formatEntities';
import {
  INNER_NODES,
  isInitializingTree,
  isCancelError,
  isNodeSelectable,
  isNodeExpandable,
  getEntityIcon,
  genEntityDisplayLabel,
  getFolderContents,
  fetchFolderPermission,
  addNodes,
  addLoadingNode,
  addRetryNode,
  cleanFolderContent,
  cleanFolderPagination,
  cancelFolderRequest,
  isBuiltInNode,
  getImplicitSelectedKeys,
  customizeFileFilter,
  getExplicitNodes,
  getTopFolders,
  getNodesByKeys,
  getNodeByKey,
  getDefaultExpandKeys,
  sortFolders,
} from '../helpers/tree';
import { differenceBy, isMalwareDetected } from '../helpers/utils';
import { useTranslation } from '../i18n';

const flexStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const viewItemStyle = {
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  flex: 'auto',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const EllipsisLabel = styled(Box)`
  max-width: 400px;
  overflow: hidden;
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PAGINATION = {
  folder: {
    limit: 200,
    offset: 0,
    hasNext: false,
    isLoadedAll: false,
  },
  file: {
    limit: 200,
    offset: 0,
    hasNext: false,
    isLoadedAll: false,
  },
};

const DEFAULT_PROPS = {
  disabledKeys: [],
  selectedKeys: [],
  dataViewProps: {
    dataFilter: undefined,
    enableFoldersSelection: false,
    allowWholeFolderSelection: false,
    rowIdsWithIndicator: [],
    renderIndicator: noop,
  },
  valueKey: 'id',
};

const TreeView = React.forwardRef(
  (
    {
      plugin,
      entities,
      disabledIds: disabledKeys = DEFAULT_PROPS.disabledKeys,
      selectedIds: selectedKeys = DEFAULT_PROPS.selectedKeys,
      setSelection,
      multiple,
      dataViewProps = DEFAULT_PROPS.dataViewProps,
      valueKey = DEFAULT_PROPS.valueKey,
      onGetCachedEntity = noop,
      ...rest
    },
    forwardedRef,
  ) => {
    const projectId = useCurrentProjectId();
    const { t } = useTranslation();
    const defaultFolderUrn = plugin.getDefaultExpandedFolderUrn?.();
    const [isInitializing, setIsInitializing] = useState(
      isInitializingTree(entities),
    );
    const [isInitializeError, setIsInitializeError] = useState(false);
    const [nodes, setNodes] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const treeRef = useRef(null);
    const paginationRef = useRef(new Map());
    const folderPermissionRef = useRef(new Map());
    const {
      dataFilter,
      enableFoldersSelection: isMixMode,
      allowWholeFolderSelection: enableSelectAll, // includes/excludes all descendants
      rowIdsWithIndicator,
      renderIndicator,
      renderEntityIcon,
      enableGetFolderPermission = false,
    } = dataViewProps;
    const implicitSelectedKeys = useMemo(
      () =>
        enableSelectAll
          ? getImplicitSelectedKeys(nodes, selectedKeys, valueKey)
          : [],
      [nodes, selectedKeys, valueKey, enableSelectAll],
    );
    const expandedIds = useMemo(
      () => expandedKeys.map(({ id, urn }) => (valueKey === 'urn' ? urn : id)),
      [expandedKeys, valueKey],
    );

    const loadContents = useCallback(
      async (node, nodes) => {
        if (node.readonly) return nodes;

        const nodePagination = paginationRef.current.get(node.urn);

        if (!nodePagination) {
          paginationRef.current.set(node.urn, {
            path: node.path,
            ...PAGINATION,
          });
          setExpandedKeys([
            ...expandedKeys,
            {
              urn: node.urn,
              path: node.path,
              id: node.id,
              idPath: node.idPath,
            },
          ]);
        }

        let permissionActions =
          folderPermissionRef.current.get(node.urn) ?? null;
        if (enableGetFolderPermission && permissionActions === null) {
          permissionActions = await fetchFolderPermission(projectId, node.urn);
          folderPermissionRef.current.set(node.urn, permissionActions);
        }

        let updatedNodes = nodes;
        try {
          const { folders, files, nextFolderPage, nextFilePage, folderUrn } =
            await getFolderContents(
              projectId,
              node,
              paginationRef.current.get(node.urn),
              permissionActions,
            );
          paginationRef.current.set(folderUrn, {
            folder: nextFolderPage,
            file: nextFilePage,
            path: node.path,
          });
          // Built-in rules run first so `dataFilter` sees them and can override any of it.
          const ruledFiles = applyBuiltInRules(files, t);
          const filteredFiles = dataFilter
            ? customizeFileFilter(ruledFiles, dataFilter)
            : ruledFiles;
          updatedNodes = addNodes(nodes, node, [...folders, ...filteredFiles]);
          setNodes(updatedNodes);
        } catch (error) {
          // swallow all error here. attach a retry node
          if (!isCancelError(error)) {
            updatedNodes = addRetryNode(nodes, node.urn);
            setNodes(updatedNodes);
          }
        }
        return updatedNodes;
      },
      [projectId, dataFilter, expandedKeys, enableGetFolderPermission, t],
    );

    useEffect(() => {
      if (!isInitializing) return;

      /**
       *  list top folders when without defaultFolderUrn
       *  list folderThee and all sub-folders and first page files in the path wen given defaultFolderUrn
       */
      setIsInitializeError(false);
      const getFolders = async () => {
        try {
          const folders = await getTopFolders(projectId, defaultFolderUrn);
          const sortedFolders = sortFolders(folders, true);
          setNodes(sortedFolders);

          let toUpdatedFolders = sortedFolders;

          if (defaultFolderUrn) {
            const target = getNodeByKey(folders, defaultFolderUrn, 'urn');
            if (target) {
              const defaultExpandKeys = getDefaultExpandKeys(target);
              setExpandedKeys(defaultExpandKeys);
              for (let i = 0; i < defaultExpandKeys.length; i++) {
                const node = defaultExpandKeys[i];
                paginationRef.current.set(node.urn, {
                  path: node.path,
                  folder: {
                    hasNext: false,
                    isLoadedAll: true, // skip load sub folders, only load documents
                  },
                  file: {
                    limit: 200,
                    offset: 0,
                    hasNext: false,
                    isLoadedAll: false,
                  },
                });
                toUpdatedFolders = await loadContents(node, toUpdatedFolders);
              }
            }

            window.requestAnimationFrame(() => {
              treeRef.current?.scrollToNode({ [valueKey]: defaultFolderUrn });
            });
          }

          setIsInitializing(false);
        } catch (err) {
          setIsInitializing(false);
          // storybook hijacks https error. no cancel error outside storybook.
          if (!isCancelError(err)) {
            setIsInitializeError(true);
          }
        }
      };
      getFolders();

      // todo: de-coupling loadContents dependency and add loadContents. currently loadContents will cause extra api if added into hooks dependency.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, isInitializing, isInitializeError, defaultFolderUrn]);

    const checkNodeDisable = useCallback(
      (node) => {
        if (isBuiltInNode(node)) return false;

        if (disabledKeys.includes(node[valueKey])) return true;

        // dataFilter may set node disabled
        if (node.disabled) return true;

        if (isMalwareDetected(node)) return true;

        if (enableSelectAll) {
          return (
            !selectedKeys.includes(node[valueKey]) &&
            selectedKeys.some((value) =>
              node[valueKey === 'id' ? 'idPath' : 'path']?.includes(value),
            )
          );
        }

        return false;
      },
      [enableSelectAll, disabledKeys, selectedKeys, valueKey],
    );

    const checkNodeSelectable = useCallback(
      (node) => isNodeSelectable(node, isMixMode),
      [isMixMode],
    );

    // strategy: load folders with high priority. If folders loaded, load files at same time.
    // which means, when all folders loaded, load first page files at once.
    const handleOpenFolder = useCallback(
      (node) => loadContents(node, nodes),
      [loadContents, nodes],
    );

    const handleSelectNodes = useCallback(
      (updatedKeys) => {
        const toSelectNodes = enableSelectAll
          ? getExplicitNodes(nodes, selectedKeys, updatedKeys, valueKey)
          : getNodesByKeys(nodes, updatedKeys, valueKey);

        // cache selected nodes, nodes tree content cleared when folder closed.
        const cachedNodes = updatedKeys
          .map((key) => onGetCachedEntity(key))
          .filter(Boolean);
        const difference = differenceBy(cachedNodes, toSelectNodes, valueKey);
        const pickedNodes = enableSelectAll
          ? [...toSelectNodes]
          : [...difference, ...toSelectNodes];

        // Filter out disabled nodes and malware files from the final selection
        const selections = pickedNodes.filter(
          (node) =>
            !disabledKeys.includes(node[valueKey]) &&
            !isMalwareDetected(node) &&
            !node.disabled,
        );
        setSelection(selections.map(formatEntityBlock));
      },
      [
        enableSelectAll,
        nodes,
        selectedKeys,
        setSelection,
        valueKey,
        disabledKeys,
        onGetCachedEntity,
      ],
    );

    const handleLoadMore = useCallback(
      (node) => {
        const updatedNodes = addLoadingNode(nodes, node.urn);
        setNodes(updatedNodes);
        loadContents(node, nodes);
      },
      [loadContents, nodes],
    );

    /**
     * Background: The VirtualizedTree manages loading/loadError UI component and ONLY inject loading/loadError state into nodeTree when expand node.
     *
     * After clicked loadMore button, this loading/loadError nodes are outside VirtualizedTree and still loading/loadError UI
     * rendered by VirtualizedTree.
     *
     * Solution:
     * 1. VirtualizedTree renders loading/loadError UI component, TreeView renders loadMore UI component.
     * 2. loadContents method swallows all errors and manages:
     *    loadError state when error happens in expand node (skip the control in the background) and load more.
     */
    const renderViewItem = useCallback(
      ({ node }) => {
        if (
          node[VirtualizedTree.NODE_KEYS.NODE_ACTION_TYPE] ===
          INNER_NODES.LOAD_MORE
        ) {
          return (
            <Button
              variant={Button.VARIANTS.TERTIARY}
              onClick={() => handleLoadMore(node)}
            >
              {t('LOAD_MORE')}
            </Button>
          );
        }
        const EntityIcon = getEntityIcon(node);
        const showCustomIndicator =
          renderIndicator && rowIdsWithIndicator.includes(node.urn);
        const showMalwareIndicator = isMalwareDetected(node);
        const { tooltipContent } = node;
        const tooltip = showMalwareIndicator
          ? t('MALWARE_DETECTED_TOOLTIP')
          : genEntityDisplayLabel(node);
        const label = genEntityDisplayLabel(node);

        return (
          <div style={viewItemStyle}>
            {renderEntityIcon ? (
              renderEntityIcon(node, EntityIcon)
            ) : (
              <EntityIcon style={{ marginRight: '8px', flexShrink: '0' }} />
            )}
            {showMalwareIndicator ? (
              <MalwareContent>
                <Tooltip content={t('MALWARE_DETECTED_TOOLTIP')}>
                  <EllipsisLabel>{label}</EllipsisLabel>
                </Tooltip>
              </MalwareContent>
            ) : tooltipContent ? (
              <Tooltip content={tooltipContent}>
                <EllipsisLabel>{label}</EllipsisLabel>
              </Tooltip>
            ) : (
              <OverflowTooltip
                content={tooltip}
                getTargetProps={() => ({
                  style: { overflow: 'hidden', width: '100%' },
                })}
              >
                {label}
              </OverflowTooltip>
            )}
            {showCustomIndicator && renderIndicator(node)}
          </div>
        );
      },
      [
        t,
        handleLoadMore,
        renderEntityIcon,
        renderIndicator,
        rowIdsWithIndicator,
      ],
    );

    const handleCloseFolder = (node) => {
      cancelFolderRequest(node.urn);
      setExpandedKeys(
        expandedKeys.filter(({ path }) => !path.includes(node.path)),
      );
      const updatedNodes = cleanFolderContent(nodes, node.urn);
      setNodes(updatedNodes);
      paginationRef.current = cleanFolderPagination(
        paginationRef.current,
        node.urn,
        node.path,
      );
      folderPermissionRef.current.delete(node.urn);
    };

    if (isInitializing) {
      return (
        <div style={flexStyle}>
          <ProgressRing size={ProgressRing.SIZES.MEDIUM} />
        </div>
      );
    }

    if (isInitializeError) {
      return (
        <div style={flexStyle}>
          <EntityPickerModalError onTryAgain={() => setIsInitializing(true)} />
        </div>
      );
    }

    return (
      <div style={{ height: '100%', overflow: 'hidden' }}>
        <VirtualizedTree
          {...rest}
          ref={(ref) => {
            treeRef.current = ref;

            if (forwardedRef) {
              typeof forwardedRef === 'function'
                ? forwardedRef(ref)
                : (forwardedRef.current = ref);
            }
          }}
          enableShiftSelection
          enableCrossParentSelection
          showSkeletonLoading
          matchAlloyStyle
          rowHeight={48}
          getNodeStyle={() => ({
            borderBottom: '1px solid rgba(238, 238, 238, 1)',
          })}
          childrenKey="folders"
          retryButtonText={t('RETRY')}
          emptyResultText={t('EMPTY')}
          valueKey={valueKey}
          multiple={multiple}
          selectedKeys={[...selectedKeys, ...implicitSelectedKeys]}
          nodes={nodes}
          expandedKeys={expandedIds}
          isNodeDisabled={checkNodeDisable}
          isNodeSelectable={checkNodeSelectable}
          isNodeExpandable={isNodeExpandable}
          onExpandNode={handleOpenFolder}
          onUnexpandNode={handleCloseFolder}
          viewItemRenderer={renderViewItem}
          onSelectNodesChange={handleSelectNodes}
        />
      </div>
    );
  },
);

TreeView.displayName = 'TreeView';

export default TreeView;
